import {NumberUtil} from "../../../../js/util/NumberUtil";
import {UserConfig} from "../../../../types/UserConfig";

export abstract class PriceStrategy {

    protected constructor() {
    }

    public abstract stepSize(limit: number): number;

    public abstract format(input: any, skipInvalidValues?: boolean): any;

    public abstract formatValueExtraPrecision(input: number | null, extraPrecision?: number): string | null;

    public abstract formatValue(input: number | null): string | null;

    public abstract formatStrike(input: number | null): string | null;

    public abstract parse(input: any): any;

    public tick(price: number, up: boolean): number {
        if (up) {
            return this.tickUp(price);
        } else {
            return this.tickDown(price);
        }
    }

    public tickUp(price: number): number {
        const step = this.stepSize(price);
        return NumberUtil.tickUpToClosest(price, step);
    }

    public tickDown(price: number): number {
        const step = this.stepSize(price);
        return NumberUtil.tickDownToClosest(price, step);
    }

    public round(price: number): number {
        const step = this.stepSize(price);
        return NumberUtil.roundToStep(price, step, true);
    }
}

export class IndexPricingStrategy extends PriceStrategy {

    public constructor() {
        super();
    }

    public stepSize(limit: number): number {
        return 1;
    }

    public formatValueExtraPrecision(input: number | null, extraPrecision: number = 0): string | null {
        if (input == null) {
            return null;
        } else {
            return input.toFixed(extraPrecision);
        }
    }

    // Slightly strange, but we want to leave invalid numbers as they are rather than truncate them in some circumstances
    public format(input: any, skipInvalidValues: boolean = false): any {
        if (input == null) {
            return null;
        } else if (!skipInvalidValues && NumberUtil.isPowerOf(input, 1)) {
            return input.toFixed(0);
        } else {
            return String(input);
        }
    }

    public formatValue(input: number | null): string | null {
        if (input == null) {
            return null;
        } else {
            return input.toFixed(1);
        }
    }

    public formatStrike(input: number | null): string | null {
        if (input == null) {
            return null;
        } else {
            return input.toFixed(0);
        }
    }

    public parse(input: any): any {
        return Number(input);
    }
}

export class EquityEtoPricingStrategy extends PriceStrategy {

    constructor(protected readonly userConfig: UserConfig) {
        super();
    }

    public stepSize(limit: number): number {
        if (Math.abs(limit) < 0.01) {
            return 0.001;
        } else {
            return 0.005;
        }
    }

    public format(input: any, skipInvalidValues: boolean = false) {
        if (input == null) {
            return null;
        } else if (this.priceInCents) {
            const value = input * 100;
            return value.toFixed(1);
        }
        return input.toFixed(3);
    }

    public formatValueExtraPrecision(input: number | null, extraPrecision: number = 0): string | null {
        if (input == null) {
            return null;
        } else if (this.priceInCents) {
            const value = input * 100;
            return value.toFixed(1 + extraPrecision);
        }
        return input.toFixed(3 + extraPrecision);

    }

    public formatValue(input: number | null): string | null {
        if (input == null) {
            return null;
        } else {
            const rounded = this.round(input);
            return this.format(rounded);
        }
    }

    public formatStrike(input: number | null): string | null {
        if (input == null) {
            return null;
        } else {
            return input.toFixed(3);
        }
    }

    public parse(input: any) {
        let value = Number(input);
        if (this.priceInCents) {
            value = value / 100;
        }
        return value;
    }

    private get priceInCents(): boolean {
        return !!this.userConfig.preferences.priceInCents;
    }
}

export class EquityPricingStrategy extends PriceStrategy {

    constructor(protected readonly userConfig: UserConfig) {
        super();
    }

    public stepSize(limit: number): number {
        if (Math.abs(limit) < 0.10) {
            return 0.001;
        } else if (Math.abs(limit) < 2.00) {
            return 0.005;
        } else {
            return 0.01;
        }
    }

    public format(input: any, skipInvalidValues: boolean = false): any {
        if (input == null) {
            return null;
        } else if (this.priceInCents) {
            const value = input * 100;
            return value.toFixed(1);
        }
        return input.toFixed(3);
    }

    public formatValueExtraPrecision(input: number | null, extraPrecision: number = 0): string | null {
        if (input == null) {
            return null;
        } else if (this.priceInCents) {
            const value = input * 100;
            return value.toFixed(1 + extraPrecision);
        }
        return input.toFixed(3 + extraPrecision);

    }

    public formatValue(input: number | null): string | null {
        if (input == null) {
            return null;
        } else {
            const rounded = this.round(input);
            return this.format(rounded);
        }
    }

    public formatStrike(input: number | null): string | null {
        if (input == null) {
            return null;
        } else {
            return input.toFixed(3);
        }
    }

    public parse(input: any): any {
        let value = Number(input);
        if (this.priceInCents) {
            value = value / 100;
        }
        return value;
    }

    private get priceInCents(): boolean {
        return !!this.userConfig.preferences.priceInCents;
    }
}

export class EquityTmcPricingStrategy extends EquityEtoPricingStrategy {

    constructor(userConfig: UserConfig) {
        super(userConfig);
    }

    public stepSize(limit: number): number {
        return 0.005;
    }
}
